import { Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { NotificationBell } from "@/components/NotificationBell";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { SettingsSidebar } from "@/components/SettingsSidebar";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { Toaster } from "@/components/ui/toaster";
import { Loader2 } from "lucide-react";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import { AccountsPage } from "@/pages/accounts/AccountsPage";
import { AccountDetailPage } from "@/pages/accounts/AccountDetailPage";
import { AccountsPage as CRMAccountsPage } from "@/pages/crm/AccountsPage";
import { AccountDetailPage as CRMAccountDetailPage } from "@/pages/crm/AccountDetailPage";
import LeadsPage from "@/pages/leads/LeadsPage";
import LeadDetailPage from "@/pages/leads/LeadDetailPage";
import ProductsPage from "@/pages/products/ProductsPage";
import ProductDetailPage from "@/pages/products/ProductDetailPage";
import ContactsPage from "@/pages/contacts/ContactsPage";
import ContactDetailPage from "@/pages/contacts/ContactDetailPage";
import QuotesPage from "@/pages/quotes/QuotesPage";
import QuoteDetailPage from "@/pages/quotes/QuoteDetailPage";
import OrdersPage from "@/pages/orders/OrdersPage";
import OrderDetailPage from "@/pages/orders/OrderDetailPage";
import OrderLineItemsPage from "@/pages/orders/OrderLineItemsPage";
import InvoicesPage from "@/pages/invoices/InvoicesPage";
import InvoiceDetailPage from "@/pages/invoices/InvoiceDetailPage";
import { VendorsPage } from "@/pages/vendors/VendorsPage";
import { VendorDetailPage } from "@/pages/vendors/VendorDetailPage";
import { VendorQuotesPage } from "@/pages/vendor-quotes/VendorQuotesPage";
import VendorQuoteDetailPage from "@/pages/vendor-quotes/VendorQuoteDetailPage";
import NewVendorQuotePage from "@/pages/vendor-quotes/NewVendorQuotePage";
import { PurchaseOrdersPage } from "@/pages/purchase-orders/PurchaseOrdersPage";
import { PurchaseOrderDetailPage } from "@/pages/purchase-orders/PurchaseOrderDetailPage";
import { GoodsReceiptsPage } from "@/pages/goods-receipts/GoodsReceiptsPage";
import GoodsReceiptDetailPage from "@/pages/goods-receipts/GoodsReceiptDetailPage";
import { ApInvoicesPage } from "@/pages/ap-invoices/ApInvoicesPage";
import ApInvoiceDetailPage from "@/pages/ap-invoices/ApInvoiceDetailPage";
import NewApInvoicePage from "@/pages/ap-invoices/NewApInvoicePage";
import { CustomerPosPage } from "@/pages/customer-pos/CustomerPosPage";
import CustomerPoDetailPage from "@/pages/customer-pos/CustomerPoDetailPage";
import { VendorPortalCatalogsPage } from "@/pages/vendor-portal/VendorPortalCatalogsPage";
import VendorPortalCatalogDetailPage from "@/pages/vendor-portal/VendorPortalCatalogDetailPage";
import OpportunitiesPage from "@/pages/opportunities/OpportunitiesPage";
import OpportunityDetailPage from "@/pages/opportunities/OpportunityDetailPage";
import { ShipmentsPage } from "@/pages/shipments/ShipmentsPage";
import { ShipmentDetailPage } from "@/pages/shipments/ShipmentDetailPage";
import InventoryPage from "@/pages/inventory/InventoryPage";
import PriceListsPage from "@/pages/price-lists/PriceListsPage";
import PriceListDetailPage from "@/pages/price-lists/PriceListDetailPage";
import ContractsPage from "@/pages/contracts/ContractsPage";
import NewContractPage from "@/pages/contracts/NewContractPage";
import ContractDetailPage from "@/pages/contracts/ContractDetailPage";
import CreditMemosPage from "@/pages/credit-memos/CreditMemosPage";
import CreditMemoDetailPage from "@/pages/credit-memos/CreditMemoDetailPage";
import NotificationsPage from "@/pages/notifications/NotificationsPage";
import ProfileSettingsPage from "@/pages/settings/ProfilePage";
import OrganizationSettingsPage from "@/pages/settings/OrganizationPage";
import CategoriesSettingsPage from "@/pages/settings/CategoriesPage";
import PaymentTermsSettingsPage from "@/pages/settings/PaymentTermsPage";
import CurrenciesSettingsPage from "@/pages/settings/CurrenciesPage";
import WarehousesSettingsPage from "@/pages/settings/WarehousesPage";
import CarriersSettingsPage from "@/pages/settings/CarriersPage";
import LocationsSettingsPage from "@/pages/settings/LocationsPage";
import ProductTypesSettingsPage from "@/pages/settings/ProductTypesPage";
import SalesStagesSettingsPage from "@/pages/settings/SalesStagesPage";
import SalesTiersSettingsPage from "@/pages/settings/SalesTiersPage";
import BlanketPosPage from "@/pages/blanket-pos/BlanketPosPage";
import BlanketPoDetailPage from "@/pages/blanket-pos/BlanketPoDetailPage";
import SalesAgreementsPage from "@/pages/sales-agreements/SalesAgreementsPage";
import SalesAgreementDetailPage from "@/pages/sales-agreements/SalesAgreementDetailPage";
import RecurringTemplatesPage from "@/pages/recurring-templates/RecurringTemplatesPage";
import CompetitorsSettingsPage from "@/pages/settings/CompetitorsPage";
import ClassificationsSettingsPage from "@/pages/settings/ClassificationsPage";
import SpecificationsSettingsPage from "@/pages/settings/SpecificationsPage";
import DocumentSequencesSettingsPage from "@/pages/settings/DocumentSequencesPage";
import ForecastsPage from "@/pages/forecasts/ForecastsPage";
import CatalogBrowserPage from "@/pages/catalog-browser/CatalogBrowserPage";
import DeliveryDetailPage from "@/pages/deliveries/DeliveryDetailPage";
import TeamSettingsPage from "@/pages/settings/TeamPage";
import NotificationsPreferencesPage from "@/pages/settings/NotificationsPreferencesPage";
import AppearancePage from "@/pages/settings/AppearancePage";
import BusinessCalendarPage from "@/pages/settings/BusinessCalendarPage";
import ApprovalRulesPage from "@/pages/settings/ApprovalRulesPage";
import WebhooksPage from "@/pages/settings/WebhooksPage";
import CodeFormatsPage from "@/pages/settings/CodeFormatsPage";
import DesignSystemPage from "@/pages/internal/DesignSystemPage";
import InboundEmailPage from "@/pages/settings/InboundEmailPage";
import OnboardingTemplatesPage from "@/pages/settings/OnboardingTemplatesPage";
import {
  MyCompanyPage,
  DataMigrationPage,
  ProductDetailSettingsPage,
  QuickBooksConnectionPage,
  QuickBooksAiWizardPage,
  QuickBooksAiImportPage,
  QuickBooksSettingsPage,
  BankFeedsPage,
  AdminOrganizationsPage,
  AdminAiUsagePage,
  AdminSchemaExplorerPage,
  AdminWorkflowsPage,
  AdminAiWorkflowsPage,
  AdminQboWebhookPage,
} from "@/pages/settings/SettingsStubPage";
import PortalOrdersPage from "@/pages/portal/PortalOrdersPage";
import PortalOrderDetailPage from "@/pages/portal/PortalOrderDetailPage";
import PortalQuotesPage from "@/pages/portal/PortalQuotesPage";
import PortalQuoteDetailPage from "@/pages/portal/PortalQuoteDetailPage";
import FinancePage from "@/pages/finance/FinancePage";
import AnalyticsPage from "@/pages/analytics/AnalyticsPage";
import PublicShareBundlePage from "@/pages/PublicShareBundlePage";
import { ActiveDraftChip } from "@/components/share-bundles/ActiveDraftChip";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function RouteFallback() {
  return (
    <div className="flex items-center justify-center w-full h-64">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function AuthenticatedRouter() {
  return (
    <Switch>
      <Route path="/" component={DashboardPage} />
      <Route path="/dashboard" component={DashboardPage} />
      <Route path="/accounts" component={AccountsPage} />
      <Route path="/accounts/:id" component={AccountDetailPage} />
      <Route path="/leads/:id" component={LeadDetailPage} />
      <Route path="/leads" component={LeadsPage} />
      <Route path="/products/:id/:tab" component={ProductDetailPage} />
      <Route path="/products/:id" component={ProductDetailPage} />
      <Route path="/products" component={ProductsPage} />
      <Route path="/contacts/:id" component={ContactDetailPage} />
      <Route path="/contacts" component={ContactsPage} />
      <Route path="/quotes" component={QuotesPage} />
      <Route path="/quotes/:id" component={QuoteDetailPage} />
      <Route path="/customer-pos/:id" component={CustomerPoDetailPage} />
      <Route path="/customer-pos" component={CustomerPosPage} />
      <Route path="/orders" component={OrdersPage} />
      <Route path="/order-line-items" component={OrderLineItemsPage} />
      <Route path="/orders/:id" component={OrderDetailPage} />
      <Route path="/invoices" component={InvoicesPage} />
      <Route path="/invoices/:id" component={InvoiceDetailPage} />
      <Route path="/vendors/:id" component={VendorDetailPage} />
      <Route path="/vendors" component={VendorsPage} />
      <Route path="/vendor-quotes/new" component={NewVendorQuotePage} />
      <Route path="/vendor-quotes/:id" component={VendorQuoteDetailPage} />
      <Route path="/vendor-quotes" component={VendorQuotesPage} />
      <Route path="/purchase-orders/:id" component={PurchaseOrderDetailPage} />
      <Route path="/purchase-orders" component={PurchaseOrdersPage} />
      <Route path="/goods-receipts/:id" component={GoodsReceiptDetailPage} />
      <Route path="/goods-receipts" component={GoodsReceiptsPage} />
      <Route path="/ap-invoices/new" component={NewApInvoicePage} />
      <Route path="/ap-invoices/:id" component={ApInvoiceDetailPage} />
      <Route path="/ap-invoices" component={ApInvoicesPage} />
      <Route path="/vendor-portal/catalogs/:id" component={VendorPortalCatalogDetailPage} />
      <Route path="/vendor-portal/catalogs" component={VendorPortalCatalogsPage} />
      <Route path="/opportunities/:id" component={OpportunityDetailPage} />
      <Route path="/opportunities" component={OpportunitiesPage} />
      <Route path="/shipments/:id" component={ShipmentDetailPage} />
      <Route path="/shipments" component={ShipmentsPage} />
      <Route path="/inventory" component={InventoryPage} />
      <Route path="/price-lists/:id" component={PriceListDetailPage} />
      <Route path="/price-lists" component={PriceListsPage} />
      <Route path="/contracts/new" component={NewContractPage} />
      <Route path="/contracts/:id" component={ContractDetailPage} />
      <Route path="/contracts" component={ContractsPage} />
      <Route path="/credit-memos/:id" component={CreditMemoDetailPage} />
      <Route path="/credit-memos" component={CreditMemosPage} />
      <Route path="/notifications" component={NotificationsPage} />
      <Route path="/blanket-pos/:id" component={BlanketPoDetailPage} />
      <Route path="/blanket-pos" component={BlanketPosPage} />
      <Route path="/sales-agreements/:id" component={SalesAgreementDetailPage} />
      <Route path="/sales-agreements" component={SalesAgreementsPage} />
      <Route path="/recurring-templates" component={RecurringTemplatesPage} />
      <Route path="/forecasts" component={ForecastsPage} />
      <Route path="/catalog-browser" component={CatalogBrowserPage} />
      <Route path="/deliveries/:id" component={DeliveryDetailPage} />
      <Route path="/settings/profile" component={ProfileSettingsPage} />
      <Route path="/settings/organization" component={OrganizationSettingsPage} />
      <Route path="/settings/categories" component={CategoriesSettingsPage} />
      <Route path="/settings/payment-terms" component={PaymentTermsSettingsPage} />
      <Route path="/settings/currencies" component={CurrenciesSettingsPage} />
      <Route path="/settings/warehouses" component={WarehousesSettingsPage} />
      <Route path="/settings/carriers" component={CarriersSettingsPage} />
      <Route path="/settings/locations" component={LocationsSettingsPage} />
      <Route path="/settings/product-types" component={ProductTypesSettingsPage} />
      <Route path="/settings/crm/sales-stages" component={SalesStagesSettingsPage} />
      <Route path="/settings/crm/sales-tiers" component={SalesTiersSettingsPage} />
      <Route path="/settings/crm/competitors" component={CompetitorsSettingsPage} />
      <Route path="/settings/classifications" component={ClassificationsSettingsPage} />
      <Route path="/settings/specifications" component={SpecificationsSettingsPage} />
      <Route path="/settings/document-sequences" component={DocumentSequencesSettingsPage} />
      <Route path="/settings/team" component={TeamSettingsPage} />
      <Route path="/settings/notifications" component={NotificationsPreferencesPage} />
      <Route path="/settings/appearance" component={AppearancePage} />
      <Route path="/settings/business-calendar" component={BusinessCalendarPage} />
      <Route path="/settings/approval-rules" component={ApprovalRulesPage} />
      <Route path="/settings/webhooks" component={WebhooksPage} />
      <Route path="/settings/code-formats" component={CodeFormatsPage} />
      <Route path="/settings/inbound-email" component={InboundEmailPage} />
      <Route path="/settings/onboarding/templates" component={OnboardingTemplatesPage} />
      {/* Stubbed routes — see ianfiles/sidebar-layout.md and SettingsStubPage.tsx */}
      <Route path="/settings/my-company" component={MyCompanyPage} />
      <Route path="/settings/data-migration" component={DataMigrationPage} />
      <Route path="/settings/product-detail" component={ProductDetailSettingsPage} />
      <Route path="/settings/quickbooks/connection" component={QuickBooksConnectionPage} />
      <Route path="/settings/quickbooks/ai-wizard" component={QuickBooksAiWizardPage} />
      <Route path="/settings/quickbooks/ai-import" component={QuickBooksAiImportPage} />
      <Route path="/settings/quickbooks/settings" component={QuickBooksSettingsPage} />
      <Route path="/settings/bank-feeds" component={BankFeedsPage} />
      <Route path="/settings/admin/organizations" component={AdminOrganizationsPage} />
      <Route path="/settings/admin/ai-usage" component={AdminAiUsagePage} />
      <Route path="/settings/admin/schema-explorer" component={AdminSchemaExplorerPage} />
      <Route path="/settings/admin/workflows" component={AdminWorkflowsPage} />
      <Route path="/settings/admin/ai-workflows" component={AdminAiWorkflowsPage} />
      <Route path="/settings/admin/qbo-webhook" component={AdminQboWebhookPage} />
      <Route path="/portal/orders/:id" component={PortalOrderDetailPage} />
      <Route path="/portal/orders" component={PortalOrdersPage} />
      <Route path="/portal/quotes/:id" component={PortalQuoteDetailPage} />
      <Route path="/portal/quotes" component={PortalQuotesPage} />
      {/* Internal design-system smoke route — see docs/design-system-page-migration.md */}
      <Route path="/internal/design" component={DesignSystemPage} />
      <Route path="/finance" component={FinancePage} />
      <Route path="/analytics" component={AnalyticsPage} />
      <Route path="/settings" component={SettingsPage} />
      <Route path="/crm/accounts" component={CRMAccountsPage} />
      <Route path="/crm/accounts/:id" component={CRMAccountDetailPage} />
      <Route>
        <div className="p-6">
          <h1 className="text-2xl font-bold">Page Not Found</h1>
          <p className="text-muted-foreground mt-2">The page you're looking for doesn't exist yet.</p>
        </div>
      </Route>
    </Switch>
  );
}

function AuthenticatedLayout() {
  const [location] = useLocation();
  const inSettings = location === "/settings" || location.startsWith("/settings/");

  // The settings sidebar is wider (rail + items column) than the main app
  // sidebar, so we hand the right width to `SidebarProvider` per-route.
  // 14rem rail + 16rem items column = 30rem when fully expanded.
  const sidebarStyle = {
    "--sidebar-width": inSettings ? "30rem" : "16rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full">
        {/* Swap the main app sidebar for the settings sidebar while on
            /settings* routes — the settings UI is its own nav surface, not
            an additional pane on top of the main nav. */}
        {inSettings ? <SettingsSidebar /> : <AppSidebar />}
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between gap-2 p-3 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <PageBreadcrumb />
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <ThemeToggle />
              {/* TODO Phase 9.x: account-menu dropdown */}
            </div>
          </header>
          <main className="flex-1 overflow-auto p-6">
            <Suspense fallback={<RouteFallback />}>
              <AuthenticatedRouter />
            </Suspense>
          </main>
        </div>
      </div>
      {/* Floating share-bundle runner — visible across every page once the
          user has an active draft. Hidden when no draft exists. */}
      <ActiveDraftChip />
    </SidebarProvider>
  );
}

function AppContent() {
  const { isLoading, isAuthenticated } = useAuth();
  const [location] = useLocation();

  // Public share pages: no auth required. Match before the auth gate.
  if (location.startsWith('/share/bundle/')) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>}>
        <Switch>
          <Route path="/share/bundle/:token" component={PublicShareBundlePage} />
        </Switch>
      </Suspense>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated && location !== '/login') {
    return <LoginPage />;
  }

  if (location === '/login' && isAuthenticated) {
    window.location.href = '/';
    return null;
  }

  if (location === '/login') {
    return <LoginPage />;
  }

  return <AuthenticatedLayout />;
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppContent />
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
